// Access & People panel — the People + Access card pair that used to live
// on the dedicated `?tab=people-access` tab. Now hosted inside a slide-over
// drawer launched from the People tile on the Details tab so the access
// surface stays one click away without crowding the tab bar.
//
// canAdmin is computed locally from the viewer's role + linked-person
// status on this contract. The Access card is only rendered for canAdmin
// viewers; the People card is always shown when the panel is open.

import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Alert, Button } from "../common";
import ContractPeopleCard from "./ContractPeopleCard";
import ContractAccessCard from "./ContractAccessCard";
import {
  useUpdateManagedContract,
} from "../../hooks/useManagedContracts";
import {
  useContractAccess,
  useGrantContractAcl,
  useRevokeContractAcl,
} from "../../hooks/useContractAcl";
import { useOrgUsers } from "../../hooks/useOrgUsers";
import { useAuth } from "../../contexts/AuthContext";
import type { ContractPerson, ManagedContract } from "../../types";

interface AccessPeoplePanelProps {
  contract: ManagedContract;
  contractId: string;
}

export default function AccessPeoplePanel({
  contract,
  contractId,
}: AccessPeoplePanelProps) {
  const { user: viewer } = useAuth();
  const updateMutation = useUpdateManagedContract();
  const grantMutation = useGrantContractAcl();
  const revokeMutation = useRevokeContractAcl();
  const { data: accessData } = useContractAccess(contractId);
  const { data: orgUsersData } = useOrgUsers();
  const orgUsers = orgUsersData?.items ?? [];

  // Server-truth people roster — the backend mapper guarantees a non-empty
  // array for every read except corrupted legacy rows that are missing
  // both PersonReference fields. The fallback to [] keeps the draft state
  // bound on those edge cases without throwing.
  const serverPeople = useMemo<ContractPerson[]>(
    () => contract.contractPeople ?? [],
    [contract.contractPeople],
  );

  const [draftPeople, setDraftPeople] = useState<ContractPerson[]>(serverPeople);
  // Last-known-good snapshot the dirty bit compares against. Initialised
  // from server, then overwritten with the mutation response on a clean
  // save so peopleDirty settles to false immediately even before the
  // parent's contract refetch lands. Without this, JSON.stringify(draft)
  // !== JSON.stringify(serverPeople) can keep Save Changes lit after a
  // successful save (the parent's contract.contractPeople still points at
  // the pre-save state until the query invalidation resolves).
  const [savedPeople, setSavedPeople] = useState<ContractPerson[]>(serverPeople);
  const [pendingGrants, setPendingGrants] = useState<string[]>([]);
  const [pendingRevokes, setPendingRevokes] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Transient success Alert — set on a successful save, auto-cleared after
  // ~3s. The timer ref is held so we can clear an in-flight dismiss when a
  // subsequent save fires (don't want a stale timeout dropping the new
  // success message early) or when the component unmounts.
  const [saveSuccess, setSaveSuccess] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // When the parent's contract data refetches (e.g. after the mutation's
  // query-invalidation lands), bring the dirty-bit snapshot forward so a
  // subsequent in-place edit on the same row still flips the bit.
  // Pattern 0: render-time guard on the stable string key avoids an effect.
  const serverPeopleKey = JSON.stringify(serverPeople);
  const [prevServerPeopleKey, setPrevServerPeopleKey] = useState(serverPeopleKey);
  if (serverPeopleKey !== prevServerPeopleKey) {
    setPrevServerPeopleKey(serverPeopleKey);
    setSavedPeople(serverPeople);
  }

  const peopleDirty = JSON.stringify(draftPeople) !== JSON.stringify(savedPeople);
  const aclDirty = pendingGrants.length > 0 || pendingRevokes.length > 0;
  const isDirty = peopleDirty || aclDirty;

  // canAdmin shadow — admin / global-admin or editor linked as owner/manager.
  const isAdminOrGlobalAdmin =
    viewer?.role === "admin" || viewer?.role === "global-admin";
  const isLinkedOwnerOrManager = serverPeople.some(
    (p) =>
      (p.role === "owner" || p.role === "manager") &&
      p.userId === viewer?.userId,
  );
  const canAdmin =
    isAdminOrGlobalAdmin ||
    (viewer?.role === "editor" && isLinkedOwnerOrManager);

  const accessUserIds = (accessData?.users ?? [])
    .map((u) => u.userId)
    // Apply pending mutations to the local snapshot so the access state
    // badges reflect the draft, not just the server.
    .filter((uid) => !pendingRevokes.includes(uid))
    .concat(pendingGrants.filter((uid) => !accessData?.users.some((u) => u.userId === uid)));

  const handleAddPerson = () => {
    setDraftPeople([
      ...draftPeople,
      {
        // Display-cased free-text label — the role string renders verbatim
        // on the Details tab People tile and in the People-card role input,
        // so a clean human label avoids any consumer down-casing logic.
        role: "Project Manager",
        name: "",
        email: undefined,
        alertsEnabled: false,
      },
    ]);
  };

  const handleSave = async () => {
    setSaveError(null);
    // Drop any stale success Alert + its pending timer before a new save so
    // a fast follow-up edit doesn't show the previous "Changes saved"
    // banner on top of an in-flight request.
    setSaveSuccess(false);
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    // Owner + manager are required exactly-once on the contract people
    // schema (backend Zod enforces). Guard at the UI layer so an
    // accidental deletion doesn't reach the server and produce a less
    // helpful error message — surface inline immediately.
    const hasOwner = draftPeople.some((p) => p.role === "owner");
    const hasManager = draftPeople.some((p) => p.role === "manager");
    if (!hasOwner) {
      setSaveError("Owner is required. Add an owner before saving.");
      return;
    }
    if (!hasManager) {
      setSaveError("Manager is required. Add a manager before saving.");
      return;
    }
    // People update first so a row-edit failure doesn't leave grant
    // fan-out half-applied against the stale roster.
    try {
      if (peopleDirty) {
        const updated = await updateMutation.mutateAsync({
          id: contractId,
          input: {
            version: contract.version,
            contractPeople: draftPeople,
          },
        });
        // Sync both the local draft and the dirty-bit snapshot from the
        // server response so peopleDirty settles to false immediately,
        // independent of whatever shape the backend normalises to (drops
        // undefineds, reorders properties, etc.). The parent's contract
        // refetch will catch up shortly; this avoids the "Save Changes
        // still lit after a successful save" visual until it does.
        const persisted = updated.contractPeople ?? [];
        setDraftPeople(persisted);
        setSavedPeople(persisted);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
      return;
    }
    // Grant + revoke fan-out with per-call partial-failure recovery: as
    // each call lands we splice the id out of the persistent pending list
    // so a subsequent retry doesn't re-fire calls that already applied.
    // On failure we surface what landed, what failed, and what's still
    // queued so the operator knows the state of the world before retrying.
    const grantsToApply = [...pendingGrants];
    const revokesToApply = [...pendingRevokes];
    const granted: string[] = [];
    const revoked: string[] = [];
    let failedAction: "grant" | "revoke" | null = null;
    let failedUserId: string | null = null;
    let failureMessage: string | null = null;

    for (const userId of grantsToApply) {
      try {
        await grantMutation.mutateAsync({ contractId, userId });
        granted.push(userId);
      } catch (err) {
        failedAction = "grant";
        failedUserId = userId;
        failureMessage = err instanceof Error ? err.message : "Grant failed";
        break;
      }
    }
    if (failedAction === null) {
      for (const userId of revokesToApply) {
        try {
          await revokeMutation.mutateAsync({ contractId, userId });
          revoked.push(userId);
        } catch (err) {
          failedAction = "revoke";
          failedUserId = userId;
          failureMessage = err instanceof Error ? err.message : "Revoke failed";
          break;
        }
      }
    }

    // Whatever landed comes out of the persistent queue — the rest stays
    // for the next retry.
    setPendingGrants((prev) => prev.filter((id) => !granted.includes(id)));
    setPendingRevokes((prev) => prev.filter((id) => !revoked.includes(id)));

    if (failedAction !== null) {
      const remainingGrants =
        grantsToApply.length - granted.length - (failedAction === "grant" ? 1 : 0);
      const remainingRevokes =
        revokesToApply.length - revoked.length - (failedAction === "revoke" ? 1 : 0);
      const parts: string[] = [];
      if (granted.length || revoked.length) {
        parts.push(
          `${granted.length} grant(s) applied, ${revoked.length} revoke(s) applied`,
        );
      }
      parts.push(
        `${failedAction} for user ${failedUserId} failed: ${failureMessage}`,
      );
      const remainingTotal = remainingGrants + remainingRevokes + 1;
      parts.push(`${remainingTotal} change(s) remaining — retry to continue.`);
      setSaveError(parts.join(". "));
      return;
    }

    // All landed cleanly — surface a transient success Alert. Auto-dismiss
    // after ~3s; the cleanup effect handles unmount, and the head of
    // handleSave drops any stale timer so a quick re-save doesn't show
    // overlapping banners.
    setSaveSuccess(true);
    successTimerRef.current = setTimeout(() => {
      setSaveSuccess(false);
      successTimerRef.current = null;
    }, 3000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        {canAdmin && (
          <Button
            variant="secondary"
            icon={<Plus className="h-4 w-4" />}
            onClick={handleAddPerson}
          >
            Add Person
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={!isDirty || updateMutation.isPending}
          loading={updateMutation.isPending}
        >
          Save Changes
        </Button>
      </div>

      {saveError && <Alert variant="error">{saveError}</Alert>}
      {saveSuccess && <Alert variant="success">Changes saved.</Alert>}

      <ContractPeopleCard
        people={draftPeople}
        orgUsers={orgUsers}
        accessUserIds={accessUserIds}
        canAdmin={canAdmin}
        onChange={setDraftPeople}
        onRemove={(index) => {
          setDraftPeople(draftPeople.filter((_, i) => i !== index));
        }}
        onGrantAccess={(userId) => {
          if (!pendingGrants.includes(userId)) {
            setPendingGrants([...pendingGrants, userId]);
          }
        }}
      />

      {canAdmin && (
        <ContractAccessCard
          accessUsers={accessData?.users ?? []}
          orgUsers={orgUsers}
          peopleUserIds={draftPeople
            .map((p) => p.userId)
            .filter((id): id is string => typeof id === "string")}
          onGrant={(userId) => {
            if (!pendingGrants.includes(userId)) {
              setPendingGrants([...pendingGrants, userId]);
            }
          }}
          onRevoke={(userId) => {
            if (!pendingRevokes.includes(userId)) {
              setPendingRevokes([...pendingRevokes, userId]);
            }
          }}
          onAddAsPerson={(user) => {
            setDraftPeople([
              ...draftPeople,
              {
                role: "Project Manager",
                name: user.name,
                email: user.email,
                userId: user.userId,
                alertsEnabled: false,
              },
            ]);
          }}
        />
      )}
    </div>
  );
}
